import { useMemo, useState } from "react";
import { MarkdownContent } from "@/components/MarkdownContent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CHART_COLORS } from "../../_components/chartColors";
import { MAX_PEERS } from "../../_components/scoring-constants";
import type { Peer } from "../../_components/scoring-types";
import { SourceChips } from "@/components/SourceChips";
import type { KpiDriver } from "./scoring-category-types";

interface DriverDetailProps {
  driver: KpiDriver;
  accountName: string;
  peerColumns: (Peer | null)[];
}

export function DriverDetail({
  driver,
  accountName,
  peerColumns,
}: DriverDetailProps) {
  const [activeTab, setActiveTab] = useState("account");

  // Build tab entries
  const tabEntries = useMemo(() => {
    const entries: Array<{
      key: string;
      label: string;
      colorIdx: number;
      score: number | null;
      reasoning: string | null;
      sources: Array<{ title: string; url: string }>;
    }> = [
      {
        key: "account",
        label: accountName,
        colorIdx: 0,
        score: driver.account_score,
        reasoning: driver.account_reasoning,
        sources: driver.account_sources,
      },
    ];

    peerColumns.slice(0, MAX_PEERS).forEach((peer, idx) => {
      if (!peer) return;
      const reasoning = driver.peer_reasonings?.[idx] ?? null;
      const sources = driver.peer_sources?.[idx] ?? [];
      entries.push({
        key: peer.id,
        label: peer.name,
        colorIdx: idx + 1,
        score: driver.peer_scores[idx] ?? null,
        reasoning,
        sources: sources ?? [],
      });
    });

    return entries;
  }, [driver, peerColumns, accountName]);

  const hasPeerTabs = tabEntries.length > 1;

  if (!hasPeerTabs) {
    // Single tab (account only) - show reasoning directly
    return (
      <div className="space-y-4">
        {driver.account_reasoning ? (
          <>
            <MarkdownContent
              content={driver.account_reasoning}
              className="prose-sm text-[0.8125rem] leading-relaxed"
            />
            <SourceChips sources={driver.account_sources} />
          </>
        ) : (
          <p className="text-muted-foreground">No analysis available</p>
        )}
      </div>
    );
  }

  // Multiple tabs - show tabbed interface
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="w-full gap-0 h-8 mb-3 bg-muted/50">
        {tabEntries.map((entry) => (
          <TabsTrigger
            key={entry.key}
            value={entry.key}
            className="text-xs gap-1.5 flex-1 min-w-0"
            title={
              entry.score != null
                ? `${entry.label} (${entry.score.toFixed(1)})`
                : entry.label
            }
          >
            <span
              className="rounded-full shrink-0 size-2"
              style={{ background: CHART_COLORS[entry.colorIdx] }}
            />
            <span className="truncate">
              {entry.label}
              {entry.score != null && (
                <span className="text-muted-foreground font-normal ml-1">
                  ({entry.score.toFixed(1)})
                </span>
              )}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>

      {tabEntries.map((entry) => (
        <TabsContent key={entry.key} value={entry.key}>
          {entry.reasoning ? (
            <MarkdownContent
              content={entry.reasoning}
              className="prose-sm text-[0.8125rem] leading-relaxed"
            />
          ) : (
            <p className="text-muted-foreground">No analysis available</p>
          )}

          <SourceChips sources={entry.sources} />
        </TabsContent>
      ))}
    </Tabs>
  );
}
